import * as React from "react";
import {
  Alert,
  Button,
  Card,
  Empty,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Space,
  Typography,
} from "antd";
import type { InputRef } from "antd";
import { PrinterOutlined } from "@ant-design/icons";
import {
  InventoryClient,
  PohodaClient,
  PositionLabelClient,
  PrintClient,
  type Product,
} from "../utils/scanner-backend";
import { ApiConfig } from "../utils/AuthorizedApiBase";
import { getApiErrorMessage, type ApiError } from "../utils/apiError";
import { extractPosition, isPositionCode, parseProductBarcode } from "../utils/barcodeUtils";
import { playAudioFeedback } from "../utils/audioFeedback";
import { useKeyboardScanner } from "../hooks/useKeyboardScanner";
import type { BarcodeScanResult, CameraStatus } from "../hooks/useBarcodeScanner";
import { CameraScanner } from "./shared/CameraScanner";
import PositionLabelSection, {
  POSITION_PATTERN,
  type PositionLabelFormValues,
  type PositionLabelSubmitHandler,
} from "./PositionLabelSection";

const { Title, Text } = Typography;

const PRINTER_PORT_OPTIONS = [
  { label: "SKLAD", value: 6101 },
  { label: "HALA", value: 6102 },
];

const pohodaClient = new PohodaClient(new ApiConfig());
const inventoryClient = new InventoryClient(new ApiConfig());
const printClient = new PrintClient(new ApiConfig());
const positionLabelClient = new PositionLabelClient(new ApiConfig());

type BatchOption = { serialNumber: string; countOnStock: number };

type Label = {
  product: Product;
  /** Every batch the product has ever had on stock, the ones on stock now first. */
  batches: BatchOption[];
  batch: string | null;
  amount: number;
  /** Packaging, printed in front of the product name ("Kartón …"). */
  note: string;
  /** The batch a scanned label named, when it has never been on stock. */
  unknownBatch: string | null;
};

/** What decides is not whether a batch is on stock, but whether the product has ever had one (S-674). */
const needsBatch = (label: Label) => label.batches.length > 0 && label.batch === null;

/** POHODA's batches, plus those our records still know after POHODA no longer lists them. */
const batchesEverOnStock = (product: Product, recorded: string[]): BatchOption[] => {
  const fromPohoda = (product.Batches ?? []).map((b) => ({ serialNumber: b.SerialNumber, countOnStock: b.CountOnStock }));
  const listedByPohoda = new Set(fromPohoda.map((b) => b.serialNumber));
  const onlyRecorded = recorded
    .filter((serialNumber) => !listedByPohoda.has(serialNumber))
    .map((serialNumber) => ({ serialNumber, countOnStock: 0 }));
  return [...fromPohoda, ...onlyRecorded].sort((a, b) => Number(b.countOnStock > 0) - Number(a.countOnStock > 0));
};

const describeLabel = (label: Label) =>
  [label.product.Name, label.batch && `šarža ${label.batch}`, `${label.amount} ${label.product.Unit}`]
    .filter(Boolean)
    .join(" · ");

type Status = { type: "info" | "warning" | "success" | "error"; message: string };

const STATUS_COLORS: Record<Status["type"], { background: string; border: string }> = {
  info: { background: "#e6f7ff", border: "#91d5ff" },
  warning: { background: "#fffbe6", border: "#ffe58f" },
  success: { background: "#f6ffed", border: "#b7eb8f" },
  error: { background: "#fff2f0", border: "#ffccc7" },
};

const handlePositionLabelSubmit: PositionLabelSubmitHandler = (position, includeArrow, printerPort) =>
  positionLabelClient.print(position, includeArrow, printerPort, null).then(() => undefined);

const ProductSearch = () => {
  const [printerPort, setPrinterPort] = React.useState(6101);
  const [code, setCode] = React.useState("");
  const [label, setLabel] = React.useState<Label | null>(null);
  const [printedLabel, setPrintedLabel] = React.useState<Label | null>(null);
  const [loadingCode, setLoadingCode] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [isPrinting, setIsPrinting] = React.useState(false);
  const [scannedPosition, setScannedPosition] = React.useState<string | null>(null);
  const [positionForm] = Form.useForm<PositionLabelFormValues>();
  const positionInForm = Form.useWatch("position", positionForm);
  const [cameraStatus, setCameraStatus] = React.useState<CameraStatus>("idle");
  const latestRequestRef = React.useRef(0);
  const codeInputRef = React.useRef<InputRef>(null);

  // Takes every format the packing screen (ReceivedOrderPicking) does: "KÓD", "KÓD#ŠARŽA#MNOŽSTVO"
  // from our labels (also "KÓD##1") and "1A14@" for a position.
  const handleCode = React.useCallback(async (rawCode: string) => {
    const scanned = rawCode.trim();
    if (!scanned) return;

    // Each scan replaces the product on screen right away, so neither a scan that fails nor one still
    // loading can leave the previous product ready to print.
    const request = ++latestRequestRef.current;
    setLabel(null);
    setError(null);
    setScannedPosition(null);

    if (isPositionCode(scanned)) {
      const position = extractPosition(scanned);
      setCode("");
      setLoadingCode(null);
      if (!POSITION_PATTERN.test(position)) {
        setError(`Neplatná pozícia: ${position}`);
        playAudioFeedback("invalidCode");
        return;
      }
      positionForm.setFields([{ name: "position", value: position, errors: [] }]);
      setScannedPosition(position);
      playAudioFeedback("positionScan");
      return;
    }

    const parsed = parseProductBarcode(scanned);
    if (!parsed) {
      setCode(scanned);
      setLoadingCode(null);
      setError(`Neplatný kód: ${scanned}`);
      playAudioFeedback("invalidCode");
      return;
    }

    setCode(parsed.productCode);
    setLoadingCode(parsed.productCode);
    try {
      const product = await pohodaClient.loadProduct(parsed.productCode);
      if (request !== latestRequestRef.current) return;
      // By POHODA's code: a code typed in lower case still finds our records.
      const recordedBatches = await inventoryClient.getBatchesEverOnStock(product.Code);
      if (request !== latestRequestRef.current) return;

      // Serial numbers count too: POHODA sends them as batches, with IsBatch off.
      const batches = batchesEverOnStock(product, recordedBatches);
      const batch = batches.some((b) => b.serialNumber === parsed.batchNumber) ? parsed.batchNumber : null;
      const loaded: Label = {
        product,
        batches,
        batch,
        amount: parsed.quantity,
        note: "",
        unknownBatch:
          parsed.batchNumber && !batch && (product.IsBatch || batches.length > 0) ? parsed.batchNumber : null,
      };
      setLabel(loaded);
      playAudioFeedback(needsBatch(loaded) ? "requireInput" : "productScan");
    } catch (e) {
      if (request !== latestRequestRef.current) return;
      setError(getApiErrorMessage(e as ApiError));
      playAudioFeedback("invalidCode");
    } finally {
      if (request === latestRequestRef.current) setLoadingCode(null);
    }
  }, [positionForm]);

  useKeyboardScanner(handleCode);

  const handleCameraScan = React.useCallback(
    (result: BarcodeScanResult) => handleCode(result.rawValue),
    [handleCode]
  );

  const updateLabel = (patch: Partial<Label>) => setLabel((current) => (current ? { ...current, ...patch } : current));

  const print = async () => {
    if (!label || needsBatch(label)) return;

    setIsPrinting(true);
    setError(null);
    try {
      await printClient.etiquette(
        (label.note ? label.note + " " : "") + label.product.Name,
        label.product.Code,
        label.batch ?? "",
        label.amount.toString(),
        label.product.Unit,
        printerPort
      );
      setPrintedLabel(label);
    } catch (e) {
      // Named, because by the time it fails the next product may already be on screen.
      setError(`Tlač zlyhala (${describeLabel(label)}): ${getApiErrorMessage(e as ApiError)}`);
      playAudioFeedback("error");
    } finally {
      setIsPrinting(false);
    }
  };

  const status: Status = error
    ? { type: "error", message: error }
    : loadingCode
      ? { type: "info", message: `Hľadám ${loadingCode}…` }
      : scannedPosition && scannedPosition === positionInForm
        ? { type: "info", message: `Pozícia ${scannedPosition} – vytlačte ju v „Tlač štítkov pozícií“` }
        : !label
          ? { type: "info", message: "Naskenujte tovar – čítačkou alebo kamerou" }
          : label === printedLabel
            ? { type: "success", message: `Odoslané na tlačiareň: ${describeLabel(label)}` }
            : needsBatch(label)
              ? { type: "warning", message: `${describeLabel(label)} – vyberte šaržu` }
              : label.product.IsBatch && !label.batch
                ? { type: "warning", message: `${describeLabel(label)} – bez šarže, tovar ešte žiadnu šaržu nemal` }
                : { type: "info", message: describeLabel(label) };

  const fieldLabelStyle: React.CSSProperties = { display: "block", fontWeight: 600, marginBottom: 4 };

  return (
    <div
      style={{
        padding: "0 8px",
        paddingBottom: cameraStatus !== "idle" ? "calc(25vh + 250px)" : "150px",
      }}
    >
      <div style={{ maxWidth: 600, margin: "0 auto" }}>
        <Space align="center" wrap style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }}>
          <Title level={2} style={{ margin: 0 }}>
            Tlač etikiet
          </Title>
          <Space size="small" align="center">
            <span style={{ fontWeight: 500, color: "#666" }}>Tlačiareň:</span>
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              options={PRINTER_PORT_OPTIONS}
              value={printerPort}
              onChange={(e) => setPrinterPort(e.target.value)}
            />
          </Space>
        </Space>

        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Input.Search
            ref={codeInputRef}
            placeholder="Kód produktu"
            enterButton="Hľadaj"
            size="large"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onSearch={(value) => {
              // The next scan must not be typed onto the end of this code.
              codeInputRef.current?.blur();
              void handleCode(value);
            }}
            loading={loadingCode !== null}
          />

          {label ? (
            <>
              <Card size="small" bodyStyle={{ padding: 12 }}>
                <Text strong style={{ fontSize: 16, display: "block" }}>
                  {label.product.Name}
                </Text>
                <Text type="secondary">{label.product.Code}</Text>
              </Card>

              {label.unknownBatch && (
                <Alert
                  type="warning"
                  showIcon
                  message={
                    label.batches.length
                      ? `Šarža ${label.unknownBatch} zo štítka na sklade nikdy nebola – vyberte šaržu.`
                      : `Šarža ${label.unknownBatch} zo štítka na sklade nikdy nebola a tovar ešte žiadnu nemal – etiketa bude bez šarže.`
                  }
                />
              )}

              {label.batches.length > 0 && (
                <div>
                  <label htmlFor="label-batch" style={fieldLabelStyle}>
                    Šarža
                  </label>
                  <Select
                    id="label-batch"
                    size="large"
                    style={{ width: "100%" }}
                    placeholder="Vyberte šaržu"
                    value={label.batch ?? undefined}
                    onChange={(serialNumber: string) => updateLabel({ batch: serialNumber, unknownBatch: null })}
                    options={label.batches.map((batch) => ({
                      value: batch.serialNumber,
                      label: (
                        <span style={{ display: "flex", justifyContent: "space-between" }}>
                          <span>{batch.serialNumber}</span>
                          <span style={{ color: "rgba(0, 0, 0, 0.45)" }}>
                            {batch.countOnStock > 0 ? `${batch.countOnStock} ${label.product.Unit}` : "nie je na sklade"}
                          </span>
                        </span>
                      ),
                    }))}
                  />
                </div>
              )}

              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <label htmlFor="label-amount" style={fieldLabelStyle}>
                    Množstvo
                  </label>
                  <InputNumber
                    id="label-amount"
                    size="large"
                    style={{ width: "100%" }}
                    min={0.01}
                    value={label.amount}
                    addonAfter={label.product.Unit}
                    // Emptied on the way to a new amount: making it 1 would show "1" and the next digit gives "13".
                    onChange={(amount) => {
                      if (amount !== null) updateLabel({ amount });
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label htmlFor="label-note" style={fieldLabelStyle}>
                    Balenie
                  </label>
                  <Input
                    id="label-note"
                    size="large"
                    placeholder="Kartón / Fľaša…"
                    value={label.note}
                    onChange={(e) => updateLabel({ note: e.target.value })}
                  />
                </div>
              </div>
            </>
          ) : (
            <Card bodyStyle={{ padding: 24 }}>
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Zatiaľ žiadny tovar" />
            </Card>
          )}

          <Card
            size="small"
            title="Tlač štítkov pozícií"
            headStyle={{ backgroundColor: "#f0d91b", color: "#29241a", fontWeight: 600 }}
          >
            <PositionLabelSection
              form={positionForm}
              onSubmit={handlePositionLabelSubmit}
              printerPort={printerPort}
            />
          </Card>
        </Space>
      </div>

      {/* Same bottom bar as Inventúra SKU / Presun tovaru: what will be printed, camera and print under the thumb. */}
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          background: "#fff",
          borderTop: "1px solid #f0f0f0",
          padding: 16,
          zIndex: 100,
          boxSizing: "border-box",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div style={{ width: "100%", maxWidth: 600 }}>
          <div
            role="status"
            style={{
              marginBottom: 12,
              padding: "8px 12px",
              backgroundColor: STATUS_COLORS[status.type].background,
              border: `1px solid ${STATUS_COLORS[status.type].border}`,
              borderRadius: 4,
              textAlign: "center",
            }}
          >
            <Text>{status.message}</Text>
          </div>

          {/* Print stays next to an open camera: read, print, read, print. */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            <CameraScanner
              onScan={handleCameraScan}
              onCameraStatusChange={setCameraStatus}
              buttonSize="large"
              style={{ flex: cameraStatus === "idle" ? "0 0 auto" : "1 1 100%" }}
            />
            <Button
              type="primary"
              size="large"
              icon={<PrinterOutlined />}
              style={{ flex: 1, height: 48 }}
              disabled={!label || needsBatch(label)}
              loading={isPrinting}
              onClick={print}
            >
              Vytlač etiketu
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductSearch;
